import { useState, useRef, useEffect } from "react";
import { X, Loader2, MailCheck, Sparkles } from "lucide-react";
import axios from "axios";
import { EMAIL_REGEX } from "@/lib/validators";

// El alta del plan Gratis se crea directamente en el backend de la app
// (no en el API de marketing). Apunta a producción; se puede sobreescribir con
// REACT_APP_APP_API_URL para probar contra test/demo.
const APP_API_URL = process.env.REACT_APP_APP_API_URL || "https://app.ekklesiaonline.app";

const FIELD =
  "mt-1.5 w-full rounded-xl border border-violet-light bg-white px-4 py-3 text-[15px] text-ink shadow-sm outline-none transition-all duration-150 focus:border-violet-fuerte focus:ring-2 focus:ring-violet-fuerte/20 placeholder:text-ink-3";
const LABEL = "block text-sm font-semibold text-ink";
const ERROR = "mt-1 text-[13px] text-red-500";

/** Convierte un `detail` de FastAPI (string | array de errores) en texto legible. */
function detailToMessage(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d && typeof d === "object" && "msg" in d ? d.msg : String(d)))
      .join(", ");
  }
  return "";
}

/**
 * Modal de alta del plan Gratis. Controlado por props (`open`/`onClose`).
 * Recoge nombre, iglesia, email y teléfono, y crea la cuenta Gratis en la app
 * (POST /api/auth/register, plan="gratis", sin contraseña → el backend envía
 * un email con las credenciales de acceso).
 */
export default function FreePlanModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", churchName: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const overlayRef = useRef(null);

  // Cierre con Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock scroll del body mientras el modal está abierto
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setForm({ name: "", churchName: "", email: "", phone: "" });
      setErrors({});
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open]);

  if (!open) return null;

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = "El nombre es obligatorio.";
    if (!form.churchName.trim()) e.churchName = "El nombre de la iglesia es obligatorio.";
    if (!form.email.trim()) e.email = "El correo es obligatorio.";
    else if (!EMAIL_REGEX.test(form.email)) e.email = "Correo no válido.";
    if (!form.phone.trim()) e.phone = "El teléfono es obligatorio.";
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${APP_API_URL}/api/auth/register`, {
        name: form.name.trim(),
        church_name: form.churchName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        plan: "gratis",
      });
      setStatus("success");
    } catch (err) {
      const httpStatus = err?.response?.status;
      const detail = detailToMessage(err?.response?.data?.detail);
      let msg;
      if (httpStatus === 400) {
        msg = detail || "Este correo ya está registrado. Inicia sesión desde la app.";
      } else if (httpStatus === 429) {
        msg = "Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.";
      } else if (httpStatus) {
        msg = detail || `No se pudo completar el registro (error ${httpStatus}).`;
      } else {
        msg = "No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.";
      }
      setErrorMsg(msg);
      setStatus("error");
    }
  }

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 z-[200] flex items-center justify-center px-4"
      style={{ background: "rgba(26,17,53,0.45)", backdropFilter: "blur(4px)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Empezar gratis"
      data-testid="free-plan-modal"
    >
      <div className="relative w-full max-w-md rounded-3xl bg-white shadow-[0_32px_80px_-16px_rgba(26,17,53,0.3)] overflow-hidden">
        {/* Top bar */}
        <div
          className="flex items-center justify-between px-6 py-5 border-b border-violet-light"
          style={{ background: "linear-gradient(135deg, #EEEBFF 0%, #F5F3FF 100%)" }}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-fuerte shadow-[0_4px_14px_-4px_rgba(74,58,138,0.4)]">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-display text-[15px] font-bold text-ink leading-tight">Empieza gratis</p>
              <p className="text-[12px] text-ink-2">Plan Gratis · Sin tarjeta de crédito</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-violet-light bg-white text-ink-3 transition-colors hover:text-ink"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {status === "success" ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid="free-plan-success">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EDF8F3]">
                <MailCheck className="h-8 w-8 text-[#2D8A5E]" />
              </div>
              <div>
                <p className="font-display text-lg font-bold text-ink">Revisa tu email</p>
                <p className="mt-2 text-[15px] text-ink-2 leading-relaxed">
                  Te hemos enviado tu acceso a <strong>{form.email.trim()}</strong>. Revisa tu
                  correo para establecer tu contraseña y entrar a tu cuenta.
                </p>
              </div>
              <button
                onClick={onClose}
                className="mt-2 inline-flex h-11 items-center justify-center rounded-xl bg-violet-fuerte px-8 text-[15px] font-semibold text-white transition-colors hover:bg-violet-oscuro"
              >
                Cerrar
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div>
                <label className={LABEL}>Nombre completo</label>
                <input
                  type="text"
                  placeholder="Tu nombre y apellidos"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className={FIELD}
                  autoComplete="name"
                  data-testid="free-plan-name"
                />
                {errors.name && <p className={ERROR}>{errors.name}</p>}
              </div>

              <div>
                <label className={LABEL}>Nombre de la iglesia</label>
                <input
                  type="text"
                  placeholder="Ej: Iglesia Gracia y Verdad"
                  value={form.churchName}
                  onChange={(e) => setForm((p) => ({ ...p, churchName: e.target.value }))}
                  className={FIELD}
                  autoComplete="organization"
                  data-testid="free-plan-church"
                />
                {errors.churchName && <p className={ERROR}>{errors.churchName}</p>}
              </div>

              <div>
                <label className={LABEL}>Correo electrónico</label>
                <input
                  type="email"
                  placeholder="pastor@miIglesia.com"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className={FIELD}
                  autoComplete="email"
                  data-testid="free-plan-email"
                />
                {errors.email && <p className={ERROR}>{errors.email}</p>}
              </div>

              <div>
                <label className={LABEL}>Teléfono</label>
                <input
                  type="tel"
                  placeholder="+34 612 345 678"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  className={FIELD}
                  autoComplete="tel"
                  data-testid="free-plan-phone"
                />
                <p className="mt-1.5 text-[13px] text-ink-3">Te escribiremos personalmente para ayudarte a configurar tu iglesia.</p>
                {errors.phone && <p className={ERROR}>{errors.phone}</p>}
              </div>

              {status === "error" && (
                <p className="rounded-xl bg-red-50 px-4 py-3 text-[13px] text-red-600" data-testid="free-plan-error">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-violet-fuerte text-[17px] font-semibold text-white shadow-[0_6px_24px_-6px_rgba(74,58,138,0.45)] transition-all duration-300 hover:bg-violet-oscuro disabled:opacity-60"
                data-testid="free-plan-submit"
              >
                {status === "loading" ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Creando tu cuenta...</>
                ) : (
                  "Empezar gratis"
                )}
              </button>

              <p className="text-center text-[12px] text-ink-3">
                Sin tarjeta · Sin compromiso · Cancela cuando quieras
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
